import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientService } from '../../../../core/services/client.service';
import { DiaryService } from '../../../../core/services/diary.service';
import { CaseService } from '../../../../core/services/case.service';
import { TodoForCreation } from '../../../../core/models/todos/todo-for-creation.model';
import { TodoForUpdation } from '../../../../core/models/todos/todo-for-updation.model';
import { TodoRequestFilter } from '../../../../core/models/todos/todo-request-filter.model';
import { ClientForListing } from '../../../../core/models/clients/client-for-listing.model';
import { DiaryForListing } from '../../../../core/models/diaries/diary-for-listing.model';
import { CaseForListing } from '../../../../core/models/case/case-for-listing.model';
import { TodoService } from '../../../../core/services/todo.service';
import { SubTodoService } from '../../../../core/services/sub-todo.service';
import { SubTodoForListing } from '../../../../core/models/subtodos/sub-todo-for-listing.model';
import { SubTodoForCreation } from '../../../../core/models/subtodos/sub-todo-for-creation.model';
import { SubTodoForCreationWithoutTodoId } from '../../../../core/models/subtodos/sub-todo-for-creation-without-todo-id.model';
import { TodoDto } from '../../../../core/models/todos/todo.model';

@Component({
  selector: 'app-todo-form',
  standalone: false,
  templateUrl: './todo-form.component.html',
  styleUrl: './todo-form.component.css'
})

export class TodoFormComponent implements OnInit {
  todoForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  todoId!: number;
  clients: ClientForListing[] = [];
  diaries: DiaryForListing[] = [];
  cases: CaseForListing[] = [];
  subTodos: SubTodoForListing[] = [];
  newSubTodos: SubTodoForCreationWithoutTodoId[] = [];
  newSubTodoDescription: string = '';

  constructor(
    private fb: FormBuilder,
    private todoService: TodoService,
    private clientService: ClientService,
    private diaryService: DiaryService,
    private caseService: CaseService,
    private subTodoService: SubTodoService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadClients();
    this.loadDiaries();
    this.loadCases();

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.todoId = +params['id'];
        this.loadTodoData(this.todoId);
      }
    });

    // Check for type query parameter
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['type'] && !this.isEditMode) {
        const type = queryParams['type'];
        this.todoForm.patchValue({
          type: type,
          priority: 'medium'
        });
      }
    });
  }

  private buildForm(): void {
    this.todoForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      dueDate: [null],
      isCompleted: [false],
      completedAt: [null],
      type: ['todo'],
      priority: ['medium'],
      person: [null],
      venue: [null],
      agenda: [null],
      startDateTime: [null],
      endDateTime: [null],
      caseId: [null],
      clientId: [null],
      diaryId: [null]
    });
  }

  private loadClients(): void {
    this.clientService.getPaginatedClients({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.clients = res.data,
      error: () => this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 })
    });
  }

  private loadDiaries(): void {
    this.diaryService.getPaginatedDiaries({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.diaries = res.data,
      error: () => this.snackBar.open('Failed to load diaries', 'Close', { duration: 3000 })
    });
  }

  private loadCases(): void {
    this.caseService.getPaginatedCases({ page: 1, pageSize: 100000 }).subscribe({
      next: (res) => this.cases = res.data,
      error: () => this.snackBar.open('Failed to load cases', 'Close', { duration: 3000 })
    });
  }

  private loadTodoData(id: number): void {
    this.todoService.getTodoById(id).subscribe({
      next: (todoData: TodoDto) => {
        this.todoForm.patchValue(todoData);
        this.subTodos = todoData.subTodos || [];
      },
      error: () => this.snackBar.open('Failed to load todo details', 'Close', { duration: 3000 })
    });
  }

  submitForm(): void {
    if (this.todoForm.invalid){
      this.isSubmited = false;
      return;
    }

    if (this.isEditMode) {
      const updatedTodo: TodoForUpdation = {
        id: this.todoId,
        ...this.todoForm.value
      };

      this.todoService.updateTodo(updatedTodo).subscribe({
        next: () => {
          // Create new subtodos if any
          if (this.newSubTodos.length > 0) {
            this.createNewSubTodos();
          } else {
            this.snackBar.open('Todo updated successfully', 'Close', { duration: 3000 });
            this.router.navigate(['/todos']);
          }
        },
        error: () => {
          this.snackBar.open('Failed to update todo', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });

    } else {
      const newTodo: TodoForCreation = {
        ...this.todoForm.value,
        subTodos: this.newSubTodos.length > 0 ? this.newSubTodos : undefined
      };

      this.todoService.createTodo(newTodo).subscribe({
        next: () => {
          this.snackBar.open('Todo added successfully', 'Close', { duration: 3000 });
          this.router.navigate(['/todos']);
        },
        error: () => {
          this.snackBar.open('Failed to add todo', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }

  addNewSubTodo(): void {
    if (this.newSubTodoDescription.trim()) {
      this.newSubTodos.push({ description: this.newSubTodoDescription.trim() });
      this.newSubTodoDescription = '';
    }
  }

  removeNewSubTodo(index: number): void {
    this.newSubTodos.splice(index, 1);
  }

  deleteSubTodo(subTodoId: number): void {
    this.subTodoService.deleteSubTodo(subTodoId).subscribe({
      next: () => {
        this.subTodos = this.subTodos.filter(st => st.id !== subTodoId);
        this.snackBar.open('Sub-todo deleted successfully', 'Close', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open('Failed to delete sub-todo', 'Close', { duration: 3000 });
      }
    });
  }

  checkSubTodo(subTodoId: number): void {
    this.subTodoService.checkSubTodo(subTodoId).subscribe({
      next: () => {
        const subTodo = this.subTodos.find(st => st.id === subTodoId);
        if (subTodo) {
          subTodo.isDone = true;
        }
        this.snackBar.open('Sub-todo checked successfully', 'Close', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Failed to check sub-todo', 'Close', { duration: 3000 });
      }
    });
  }

  uncheckSubTodo(subTodoId: number): void {
    this.subTodoService.uncheckSubTodo(subTodoId).subscribe({
      next: () => {
        const subTodo = this.subTodos.find(st => st.id === subTodoId);
        if (subTodo) {
          subTodo.isDone = false;
        }
        this.snackBar.open('Sub-todo unchecked successfully', 'Close', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Failed to uncheck sub-todo', 'Close', { duration: 3000 });
      }
    });
  }

  private createSubTodosForNewTodo(todoId: number): void {
    let completed = 0;
    let total = this.newSubTodos.length;

    if (total === 0) {
      this.snackBar.open('Todo added successfully', 'Close', { duration: 3000 });
      this.router.navigate(['/todos']);
      return;
    }

    this.newSubTodos.forEach(subTodo => {
      const subTodoForCreation: SubTodoForCreation = {
        todoId: todoId,
        description: subTodo.description
      };

      this.subTodoService.addSubTodo(subTodoForCreation).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.snackBar.open('Todo and sub-todos added successfully', 'Close', { duration: 3000 });
            this.router.navigate(['/todos']);
          }
        },
        error: () => {
          completed++;
          if (completed === total) {
            this.snackBar.open('Todo added but some sub-todos failed', 'Close', { duration: 3000 });
            this.router.navigate(['/todos']);
          }
        }
      });
    });
  }

  private createNewSubTodos(): void {
    let completed = 0;
    let total = this.newSubTodos.length;

    if (total === 0) {
      this.snackBar.open('Todo updated successfully', 'Close', { duration: 3000 });
      this.router.navigate(['/todos']);
      return;
    }

    this.newSubTodos.forEach(subTodo => {
      const subTodoForCreation: SubTodoForCreation = {
        todoId: this.todoId,
        description: subTodo.description
      };

      this.subTodoService.addSubTodo(subTodoForCreation).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.snackBar.open('Todo and sub-todos updated successfully', 'Close', { duration: 3000 });
            this.router.navigate(['/todos']);
          }
        },
        error: () => {
          completed++;
          if (completed === total) {
            this.snackBar.open('Todo updated but some sub-todos failed', 'Close', { duration: 3000 });
            this.router.navigate(['/todos']);
          }
        }
      });
    });
  }
}

